import { useEffect, useState } from 'react';
import {
  Braces,
  Camera,
  Check,
  ChevronRight,
  FileSpreadsheet,
  History,
  Loader2,
  Plug,
} from 'lucide-react';
import { getCurrentWorkspaceId } from '../../../lib/supabaseSync';
import {
  resolveConnectorStatuses,
  type ConnectorStatus,
} from '../../../lib/connectorConfigsService';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { canUseAnthropicVision } from '../../../lib/aiProviderKeys';

export type ItemImportSource =
  | 'excel-csv'
  | 'whiteboard'
  | 'whiteboard-recapture'
  | 'json'
  | 'jira'
  | 'miro'
  | 'notion'
  | 'linear';

export const MiroIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
    <path d="M17.392 2h-5.11l4.212 7.147L11.362 22h5.083l5.138-12.853L17.392 2zm-9.804 0H2.417l5.171 20h5.027l-5.027-20z" />
  </svg>
);

export const JiraIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
    <path d="M11.571 11.513H0a5.218 5.218 0 0 0 5.232 5.215h2.13v2.057A5.215 5.215 0 0 0 12.575 24V12.518a1.005 1.005 0 0 0-1.005-1.005zm5.723-5.756H5.736a5.215 5.215 0 0 0 5.215 5.214h2.129v2.058a5.218 5.218 0 0 0 5.215 5.214V6.758a1.001 1.001 0 0 0-1.001-1.001zM23.013 0H11.455a5.215 5.215 0 0 0 5.215 5.215h2.129v2.057A5.215 5.215 0 0 0 24 12.483V1.005A1.005 1.005 0 0 0 23.013 0z" />
  </svg>
);

export const NotionIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
    <path d="M4.459 4.208c.746.606 1.026.56 2.428.466l13.215-.793c.28 0 .047-.28-.046-.326L17.86 1.968c-.42-.326-.98-.7-2.05-.607L3.01 2.295c-.466.046-.56.28-.374.466zm.793 3.08v13.904c0 .747.373 1.027 1.214.98l14.523-.84c.841-.046.935-.56.935-1.167V6.354c0-.606-.233-.933-.748-.887l-15.177.887c-.56.047-.747.327-.747.933zm14.337.745c.093.42 0 .84-.42.888l-.7.14v10.264c-.608.327-1.168.514-1.635.514-.748 0-.935-.234-1.495-.933l-4.577-7.186v6.952l1.448.328s0 .84-1.168.84l-3.222.186c-.093-.187 0-.653.327-.746l.84-.233V9.854L7.822 9.76c-.094-.42.14-1.026.793-1.073l3.456-.233 4.764 7.279v-6.44l-1.215-.14c-.093-.514.28-.887.747-.933zM1.936 1.035l13.31-.98c1.634-.14 2.055-.047 3.082.7l4.249 2.986c.7.513.934.653.934 1.213v16.378c0 1.026-.373 1.634-1.68 1.726l-15.458.934c-.98.047-1.448-.093-1.962-.746l-3.129-4.06c-.56-.747-.793-1.306-.793-1.96V2.667c0-.839.374-1.54 1.415-1.632z" />
  </svg>
);

export const LinearIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
    <path d="M2.886 4.18A11.982 11.982 0 0 1 11.99 0C18.624 0 24 5.376 24 12.009c0 3.64-1.62 6.903-4.18 9.105L2.886 4.18ZM1.372 6.323A11.951 11.951 0 0 0 0 11.991c0 6.633 5.376 12.009 12.009 12.009 2.086 0 4.048-.532 5.759-1.467L1.372 6.323ZM.554 8.775 15.05 23.271a12.06 12.06 0 0 1-3.643.729L.554 12.416a12.06 12.06 0 0 1 0-3.641Zm.302 6.375 8.144 8.144a12.03 12.03 0 0 1-8.144-8.144Z" />
  </svg>
);

interface SourceCardProps {
  icon: React.ReactNode;
  label: string;
  description: string;
  onClick: () => void;
  badge?: string;
  connectorStatus?: ConnectorStatus | 'loading';
  onConnect?: () => void;
}

function SourceCard({
  icon,
  label,
  description,
  onClick,
  badge,
  connectorStatus,
  onConnect,
}: SourceCardProps) {
  const disconnected =
    connectorStatus === 'available' || connectorStatus === 'disabled';

  return (
    <div className="w-full flex items-center gap-4 p-4 border border-[var(--rule)] bg-[var(--bg-raised)] transition-colors hover:border-primary-400 hover:bg-primary-50/40">
      <div className="flex-shrink-0 w-10 h-10 bg-[var(--hover-tint)] flex items-center justify-center text-[var(--text-sec)]">
        {icon}
      </div>
      <button
        type="button"
        onClick={onClick}
        disabled={disconnected || connectorStatus === 'loading'}
        className="flex-1 min-w-0 text-left disabled:cursor-default"
      >
        <span className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-[var(--text-pri)]">{label}</span>
          {badge && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-[var(--mustard-dim)] text-[var(--mustard)] font-medium">
              {badge}
            </span>
          )}
          {connectorStatus === 'active' && (
            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] border border-[var(--green)] font-medium">
              <Check className="h-3 w-3" />
              Connected
            </span>
          )}
          {disconnected && (
            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-[var(--mustard-dim)] text-[var(--mustard)] border border-[var(--mustard)] font-medium">
              <Plug className="h-3 w-3" />
              Not connected
            </span>
          )}
        </span>
        <span className="block text-xs text-[var(--text-ter)] mt-0.5">{description}</span>
      </button>
      {connectorStatus === 'loading' ? (
        <Loader2 className="h-4 w-4 text-[var(--text-ter)] animate-spin flex-shrink-0" />
      ) : disconnected && onConnect ? (
        <button
          type="button"
          onClick={onConnect}
          className="flex-shrink-0 px-3 py-1.5 text-xs font-medium text-primary-700 bg-primary-50 hover:bg-primary-100 border border-primary-200"
        >
          Connect
        </button>
      ) : (
        <ChevronRight className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0" />
      )}
    </div>
  );
}

interface ItemImportSourceChooserProps {
  onSelect: (source: ItemImportSource) => void;
  onConnectConnector?: (connector: 'jira' | 'miro' | 'notion' | 'linear') => void;
  /** @deprecated Prefer omitting — chooser resolves Claude availability itself. */
  hasAnthropicKey?: boolean;
  hasSavedBoards?: boolean;
}

export function ItemImportSourceChooser({
  onSelect,
  onConnectConnector,
  hasAnthropicKey,
  hasSavedBoards = false,
}: ItemImportSourceChooserProps) {
  const userId = useUserStore((state) => state.userId);
  const [statuses, setStatuses] = useState<Record<'jira' | 'miro' | 'notion' | 'linear', ConnectorStatus | 'loading'>>({
    jira: 'loading',
    miro: 'loading',
    notion: 'loading',
    linear: 'loading',
  });
  const claudeReady = hasAnthropicKey ?? canUseAnthropicVision();

  useEffect(() => {
    let active = true;
    const workspaceId = getCurrentWorkspaceId();
    if (!workspaceId) {
      setStatuses({ jira: 'disabled', miro: 'disabled', notion: 'disabled', linear: 'disabled' });
      return;
    }

    resolveConnectorStatuses(workspaceId, userId ?? null, ['jira', 'miro', 'notion', 'linear'])
      .then((result) => {
        if (active) setStatuses({ jira: result.jira, miro: result.miro, notion: result.notion, linear: result.linear });
      })
      .catch(() => {
        if (active) setStatuses({ jira: 'disabled', miro: 'disabled', notion: 'disabled', linear: 'disabled' });
      });

    return () => {
      active = false;
    };
  }, [userId]);

  return (
    <div className="space-y-3">
      <SourceCard
        icon={<FileSpreadsheet className="h-5 w-5" />}
        label="Excel / CSV"
        description="Upload an .xlsx, .xls, or .csv file. Download a template to see all supported columns."
        onClick={() => onSelect('excel-csv')}
      />
      <SourceCard
        icon={<Camera className="h-5 w-5" />}
        label="Whiteboard Photo"
        description="Drag-drop, choose a file, or take a photo — boxes become items, arrows become relationships."
        badge={claudeReady ? 'Beta' : 'Claude key required'}
        onClick={() => onSelect('whiteboard')}
      />
      {hasSavedBoards && (
        <SourceCard
          icon={<History className="h-5 w-5" />}
          label="Update a Board from a Photo"
          description="Re-photograph a board you imported before — keep what's there and fold in what changed."
          badge={claudeReady ? 'Beta' : 'Claude key required'}
          onClick={() => onSelect('whiteboard-recapture')}
        />
      )}
      <SourceCard
        icon={<Braces className="h-5 w-5" />}
        label="Paste JSON"
        description="Paste a JSON array of objects. Each object maps to one item — only title is required."
        onClick={() => onSelect('json')}
      />
      <SourceCard
        icon={<JiraIcon />}
        label="Jira"
        description="Pull issues from a Jira project and create them as items here."
        connectorStatus={statuses.jira}
        onClick={() => onSelect('jira')}
        onConnect={() => onConnectConnector?.('jira')}
      />
      {/* Miro → ItemImportExportModal: Template Mode (zones) must stay default.
          Do not rewrite this tile to imply flat sticky-only import. */}
      <SourceCard
        icon={<MiroIcon />}
        label="Miro"
        description="Import sticky notes with spatial zone metadata (template) or as a flat list."
        connectorStatus={statuses.miro}
        onClick={() => onSelect('miro')}
        onConnect={() => onConnectConnector?.('miro')}
      />
      <SourceCard
        icon={<NotionIcon />}
        label="Notion"
        description="Import a Notion database as items — with relationships from relation properties preserved."
        connectorStatus={statuses.notion}
        onClick={() => onSelect('notion')}
        onConnect={() => onConnectConnector?.('notion')}
      />
      <SourceCard
        icon={<LinearIcon />}
        label="Linear"
        description="Import a Linear team as items — issue relations (blocks / sub-issues) become canonical relationships."
        connectorStatus={statuses.linear}
        onClick={() => onSelect('linear')}
        onConnect={() => onConnectConnector?.('linear')}
      />
    </div>
  );
}
